'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Button } from '@myapp/ui/components/button';
import { 
  FileText, 
  Shield, 
  Calendar, 
  CheckCircle,
  AlertTriangle,
  Mail,
  Phone,
  Building
} from 'lucide-react';

export default function TermsPage() {
  const [activeTab, setActiveTab] = useState('terms');

  const lastUpdated = "2024년 12월 12일";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 border-white/30 text-white">
              <Shield className="w-3 h-3 mr-1" />
              법적 고지사항
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              이용약관 및 개인정보처리방침
            </h1>
            <p className="text-xl text-blue-50 mb-8">
              강원푸드핏 서비스 이용에 관한 법적 내용을 안내드립니다
            </p>
            <div className="flex items-center justify-center gap-2 text-sm">
              <Calendar className="w-4 h-4" />
              <span>최종 업데이트: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b py-4 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant={activeTab === 'terms' ? "default" : "outline"}
              onClick={() => setActiveTab('terms')}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              이용약관
            </Button>
            <Button
              variant={activeTab === 'privacy' ? "default" : "outline"}
              onClick={() => setActiveTab('privacy')}
              className="flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              개인정보처리방침
            </Button>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {activeTab === 'terms' && (
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    강원푸드핏 서비스 이용약관
                  </CardTitle>
                  <p className="text-gray-600">본 약관은 JHLAB(이하 "회사")에서 제공하는 강원푸드핏 서비스 이용에 관한 조건을 규정합니다.</p>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none space-y-6">
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>제1조 (목적)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <p>본 약관은 JHLAB(이하 "회사")에서 운영하는 강원푸드핏 웹사이트 및 관련 서비스(이하 "서비스")를 이용함에 있어 회사와 이용자의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제2조 (정의)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li><strong>"서비스"</strong>란 회사가 운영하는 강원푸드핏 플랫폼을 통해 제공하는 모든 서비스를 의미합니다.</li>
                        <li><strong>"이용자"</strong>란 본 약관에 따라 회사가 제공하는 서비스를 받는 회원 및 비회원을 말합니다.</li>
                        <li><strong>"회원"</strong>이란 회사에 개인정보를 제공하여 회원등록을 한 자로서, 회사의 정보를 지속적으로 제공받으며, 회사가 제공하는 서비스를 계속적으로 이용할 수 있는 자를 말합니다.</li>
                        <li><strong>"비회원"</strong>이란 회원에 가입하지 않고 회사가 제공하는 서비스를 이용하는 자를 말합니다.</li>
                        <li><strong>"농산물"</strong>이란 서비스를 통해 판매되는 강원도 지역 특산물 및 관련 상품을 의미합니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제3조 (약관의 효력 및 변경)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>본 약관은 서비스 화면에 게시하거나 기타의 방법으로 회원에게 공지함으로써 효력을 발생합니다.</li>
                        <li>회사는 합리적인 사유가 발생할 경우에는 관련 법령에 위배되지 않는 범위에서 본 약관을 개정할 수 있습니다.</li>
                        <li>회사가 약관을 개정할 경우에는 적용일자 및 개정사유를 명시하여 현행약관과 함께 서비스 초기화면에 그 적용일자 7일 이전부터 적용일자 전일까지 공지합니다.</li>
                        <li>회원은 변경된 약관에 대해 동의하지 않을 권리가 있으며, 변경된 약관에 동의하지 않을 경우 서비스 이용을 중단하고 회원탈퇴를 할 수 있습니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제4조 (회원가입)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>이용자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 본 약관에 동의한다는 의사표시를 함으로써 회원가입을 신청합니다.</li>
                        <li>회사는 제1항과 같이 회원으로 가입할 것을 신청한 이용자 중 다음 각호에 해당하지 않는 한 회원으로 등록합니다:
                          <ul>
                            <li>가입신청자가 본 약관에 의하여 이전에 회원자격을 상실한 적이 있는 경우</li>
                            <li>등록 내용에 허위, 기재누락, 오기가 있는 경우</li>
                            <li>기타 회원으로 등록하는 것이 회사의 기술상 현저히 지장이 있다고 판단되는 경우</li>
                          </ul>
                        </li>
                        <li>회원가입계약의 성립시기는 회사의 승낙이 회원에게 도달한 시점으로 합니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제5조 (서비스 이용)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>서비스 이용은 회사의 업무상 또는 기술상 특별한 지장이 없는 한 연중무휴, 1일 24시간 운영을 원칙으로 합니다.</li>
                        <li>회사는 컴퓨터 등 정보통신설비의 보수점검, 교체 및 고장, 통신두절 또는 운영상 상당한 이유가 있는 경우 서비스의 제공을 일시적으로 중단할 수 있습니다.</li>
                        <li>회사는 서비스의 제공에 필요한 경우 정기점검을 실시할 수 있으며, 정기점검시간은 서비스제공화면에 공지한 바에 따릅니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제6조 (구매 및 결제)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 이용자가 다음과 같은 절차에 의하여 구매를 신청하도록 할 수 있습니다:
                          <ul>
                            <li>농산물의 검색 및 선택</li>
                            <li>수령인의 성명, 주소, 전화번호, 전자우편주소(또는 이동전화번호) 등의 입력</li>
                            <li>약관내용, 청약철회권이 제한되는 서비스, 배송료·설치비 등의 비용부담과 관련한 내용에 대한 확인</li>
                            <li>본 약관에 동의하고 위 내용을 확인하거나 거부하는 표시(예, 마우스 클릭)</li>
                            <li>농산물의 구매신청 및 이에 관한 확인 또는 회사의 확인에 대한 동의</li>
                            <li>결제방법의 선택</li>
                          </ul>
                        </li>
                        <li>회사가 제3자에게 구매자 개인정보를 제공할 필요가 있는 경우 실제 구매신청 시에 구매자에게 제공받는 자, 제공받는 자의 이용목적, 제공하는 정보의 내용을 고지하고 동의를 받아야 합니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제7조 (배송 및 반품)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 이용자와 농산물의 공급시기에 관하여 별도의 약정이 없는 이상, 이용자가 청약을 한 날부터 7일 이내에 농산물 등을 배송할 수 있도록 주문제작, 포장 등 기타의 필요한 조치를 취합니다.</li>
                        <li>회사는 이용자가 구매한 농산물에 대해 배송수단, 수단별 배송비용 부담자, 수단별 배송기간 등을 명시합니다.</li>
                        <li>농산물의 특성상 단순변심에 의한 교환·반품은 농산물을 공급받은 날로부터 7일 이내에 가능하며, 신선도가 떨어진 경우 교환·반품이 제한될 수 있습니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제8조 (환불)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 이용자가 구매신청한 농산물이 품절 등의 사유로 인도 또는 제공을 할 수 없을 때에는 지체 없이 그 사유를 이용자에게 통지하고 사전에 농산물대금을 받은 경우에는 대금을 받은 날부터 3영업일 이내에 환급하거나 환급에 필요한 조치를 취합니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제9조 (면책조항)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.</li>
                        <li>회사는 이용자의 귀책사유로 인한 서비스 이용의 장애에 대하여는 책임을 지지 않습니다.</li>
                        <li>회사는 이용자가 서비스를 이용하여 기대하는 수익을 얻지 못하거나 상실한 것에 대하여 책임을 지지 않습니다.</li>
                        <li>회사는 이용자가 서비스에 게재한 정보, 자료, 사실의 신뢰도, 정확성 등 내용에 관하여는 책임을 지지 않습니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제10조 (분쟁해결)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 이용자가 제기하는 정당한 의견이나 불만을 반영하고 그 피해를 보상처리하기 위하여 피해보상처리기구를 설치·운영합니다.</li>
                        <li>회사와 이용자 간에 발생한 전자상거래 분쟁에 관한 소송은 제소 당시의 이용자의 주소에 의하고, 주소가 없는 경우에는 거주지를 관할하는 지방법원의 전속관할로 합니다. 다만, 제소 당시 이용자의 주소 또는 거주지가 분명하지 않거나 외국거주자의 경우에는 민사소송법상의 관할법원에 제기합니다.</li>
                        <li>회사와 이용자 간에 제기된 전자상거래 소송에는 한국법을 적용합니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    개인정보처리방침
                  </CardTitle>
                  <p className="text-gray-600">강원푸드핏은 이용자의 개인정보를 중요시하며, 개인정보 보호와 관련한 법령을 준수합니다.</p>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none space-y-6">

                  <Card>
                    <CardHeader>
                      <CardTitle>제1조 (개인정보의 처리목적)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <p>JHLAB(이하 '회사')는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
                      
                      <div className="mt-4 p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold mb-2">처리목적</h4>
                        <ul className="space-y-1 text-sm">
                          <li>• 홈페이지 회원가입 및 관리</li>
                          <li>• 재화 또는 서비스 제공</li>
                          <li>• 마케팅 및 광고에의 활용</li>
                          <li>• 고충처리 및 민원사무 처리</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제2조 (개인정보의 처리 및 보유기간)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</li>
                        <li>각각의 개인정보 처리 및 보유 기간은 다음과 같습니다:</li>
                      </ol>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold mb-2">홈페이지 회원가입 및 관리</h4>
                          <ul className="space-y-1 text-sm">
                            <li><strong>보유근거:</strong> 정보주체의 동의</li>
                            <li><strong>보유기간:</strong> 회원탈퇴 시까지</li>
                            <li><strong>예외:</strong> 관계 법령 위반에 따른 수사·조사 등이 진행중인 경우에는 해당 수사·조사 종료시까지</li>
                          </ul>
                        </div>
                        
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <h4 className="font-semibold mb-2">재화 또는 서비스 제공</h4>
                          <ul className="space-y-1 text-sm">
                            <li><strong>보유근거:</strong> 계약이행 및 대금결제, 재화 등 공급</li>
                            <li><strong>보유기간:</strong> 계약이행 완료일로부터 5년</li>
                            <li><strong>법정보유:</strong> 전자상거래법에 따른 거래기록 보존</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제3조 (처리하는 개인정보 항목)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <p>회사는 다음의 개인정보 항목을 처리하고 있습니다:</p>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold mb-2">필수항목</h4>
                          <p className="text-sm text-gray-700">이름, 이메일주소, 휴대전화번호, 주소, 비밀번호, 로그인ID</p>
                        </div>
                        
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold mb-2">선택항목</h4>
                          <p className="text-sm text-gray-700">생년월일, 성별, 선호 식재료</p>
                        </div>
                        
                        <div className="p-4 bg-yellow-50 rounded-lg">
                          <h4 className="font-semibold mb-2">자동수집항목</h4>
                          <p className="text-sm text-gray-700">IP주소, 쿠키, MAC주소, 서비스 이용기록, 방문기록, 불량 이용기록 등</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제4조 (개인정보의 제3자 제공)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 정보주체의 동의, 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</li>
                        <li>회사는 다음과 같이 개인정보를 제3자에게 제공하고 있습니다:</li>
                      </ol>
                      
                      <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                        <h4 className="font-semibold mb-2">배송업체 제공</h4>
                        <ul className="space-y-1 text-sm">
                          <li><strong>제공받는 자:</strong> 택배업체 (CJ대한통운, 로젠택배 등)</li>
                          <li><strong>제공목적:</strong> 상품 배송 서비스 제공</li>
                          <li><strong>제공항목:</strong> 성명, 주소, 전화번호</li>
                          <li><strong>보유기간:</strong> 배송완료 후 즉시 파기</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제5조 (개인정보처리 위탁)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:</li>
                      </ol>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-indigo-50 rounded-lg">
                          <h4 className="font-semibold mb-2">결제처리 서비스</h4>
                          <ul className="space-y-1 text-sm">
                            <li><strong>위탁받는 자:</strong> 결제대행업체</li>
                            <li><strong>위탁업무:</strong> 신용카드, 계좌이체 등 결제처리</li>
                            <li><strong>위탁기간:</strong> 결제완료시까지</li>
                          </ul>
                        </div>
                        
                        <div className="p-4 bg-teal-50 rounded-lg">
                          <h4 className="font-semibold mb-2">고객상담 서비스</h4>
                          <ul className="space-y-1 text-sm">
                            <li><strong>위탁받는 자:</strong> 고객센터 운영업체</li>
                            <li><strong>위탁업무:</strong> 고객 문의응답, 불만처리</li>
                            <li><strong>위탁기간:</strong> 위탁계약 종료시까지</li>
                          </ul>
                        </div>
                      </div>
                      
                      <ol start="2" className="mt-4">
                        <li>회사는 위탁계약 체결시 개인정보보호법 제26조에 따라 위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치, 재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한 사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독하고 있습니다.</li>
                        <li>위탁업무의 내용이나 수탁자가 변경될 경우에는 지체없이 본 개인정보 처리방침을 통하여 공개하도록 하겠습니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제6조 (정보주체의 권리·의무 및 행사방법)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:</li>
                      </ol>
                      
                      <div className="mt-4 p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold mb-2">정보주체의 권리</h4>
                        <ul className="space-y-1 text-sm">
                          <li>• 개인정보 처리현황 통지요구</li>
                          <li>• 개인정보 열람요구</li>
                          <li>• 개인정보 오류 등이 있을 경우 정정·삭제요구</li>
                          <li>• 개인정보 처리정지요구</li>
                        </ul>
                      </div>
                      
                      <ol start="2" className="mt-4">
                        <li>제1항에 따른 권리 행사는 회사에 대해 개인정보보호법 시행령 제41조제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 회사는 이에 대해 지체없이 조치하겠습니다.</li>
                        <li>제1항에 따른 권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수 있습니다. 이 경우 개인정보보호법 시행규칙 별지 제11호 서식에 따른 위임장을 제출하셔야 합니다.</li>
                        <li>개인정보 열람 및 처리정지 요구는 개인정보보호법 제35조 제4항, 제37조 제2항에 의하여 정보주체의 권리가 제한될 수 있습니다.</li>
                      </ol>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제7조 (개인정보의 파기)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</li>
                        <li>정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여 보존합니다.</li>
                        <li>개인정보 파기의 절차 및 방법은 다음과 같습니다:</li>
                      </ol>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-red-50 rounded-lg">
                          <h4 className="font-semibold mb-2">파기절차</h4>
                          <p className="text-sm text-gray-700">회사는 파기 사유가 발생한 개인정보를 선정하고, 회사의 개인정보보호책임자의 승인을 받아 개인정보를 파기합니다.</p>
                        </div>
                        
                        <div className="p-4 bg-red-50 rounded-lg">
                          <h4 className="font-semibold mb-2">파기방법</h4>
                          <ul className="space-y-1 text-sm text-gray-700">
                            <li>• 전자적 파일 형태: 기록을 재생할 수 없도록 로우레벨포멧(Low Level Format) 등의 방법 이용</li>
                            <li>• 종이 문서: 분쇄기로 분쇄하거나 소각을 통하여 파기</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제8조 (개인정보보호책임자)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <p>회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보보호책임자를 지정하고 있습니다:</p>
                      
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2">개인정보보호책임자</h4>
                        <ul className="space-y-1 text-sm">
                          <li><strong>성명:</strong> 김성호</li>
                          <li><strong>직책:</strong> 개인정보보호책임자</li>
                          <li><strong>연락처:</strong> sungho22@hallym.ac.kr</li>
                          <li><strong>전화:</strong> 010-9646-2558</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제9조 (개인정보의 안전성 확보조치)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <p>회사는 개인정보보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 기술적/관리적 및 물리적 조치를 하고 있습니다:</p>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold mb-2">개인정보 취급 직원의 최소화 및 교육</h4>
                          <p className="text-sm text-gray-700">개인정보를 취급하는 직원을 지정하고 담당자에 한정시켜 최소화하여 개인정보를 관리하는 대책을 시행하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <h4 className="font-semibold mb-2">정기적인 자체 감사 실시</h4>
                          <p className="text-sm text-gray-700">개인정보 취급 관련 안정성 확보를 위해 정기적(분기 1회)으로 자체 감사를 실시하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-green-50 rounded-lg">
                          <h4 className="font-semibold mb-2">개인정보의 암호화</h4>
                          <p className="text-sm text-gray-700">개인정보는 암호화 등을 통해 안전하게 저장 및 관리되고 있습니다. 또한 중요한 데이터는 저장 시 별도의 보안기능을 사용하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-yellow-50 rounded-lg">
                          <h4 className="font-semibold mb-2">해킹 등에 대비한 기술적 대책</h4>
                          <p className="text-sm text-gray-700">회사는 해킹이나 컴퓨터 바이러스 등에 의한 개인정보 유출 및 훼손을 막기 위하여 보안프로그램을 설치하고 주기적인 갱신·점검을 하며 외부로부터 접근이 통제된 구역에 시스템을 설치하고 기술적/물리적으로 감시 및 차단하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-red-50 rounded-lg">
                          <h4 className="font-semibold mb-2">개인정보에 대한 접근 제한</h4>
                          <p className="text-sm text-gray-700">개인정보를 처리하는 데이터베이스시스템에 대한 접근권한의 부여, 변경, 말소를 통하여 개인정보에 대한 접근통제를 위하여 필요한 조치를 하고 있으며 침입차단시스템을 이용하여 외부로부터의 무단 접근을 통제하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold mb-2">접속기록의 보관 및 위변조 방지</h4>
                          <p className="text-sm text-gray-700">개인정보처리시스템에 접속한 기록을 최소 1년 이상 보관, 관리하고 있으며, 접속 기록이 위변조 및 도난, 분실되지 않도록 보안기능을 사용하고 있습니다.</p>
                        </div>
                        
                        <div className="p-4 bg-indigo-50 rounded-lg">
                          <h4 className="font-semibold mb-2">문서보안을 위한 잠금장치 사용</h4>
                          <p className="text-sm text-gray-700">개인정보가 포함된 서류, 보조저장매체 등을 잠금장치가 있는 안전한 장소에 보관하고 있습니다.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>제10조 (개인정보 자동 수집 장치의 설치·운영 및 거부에 관한 사항)</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <ol>
                        <li>회사는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 이용정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용합니다.</li>
                        <li>쿠키는 웹사이트를 운영하는데 이용되는 서버(HTTP)가 이용자의 컴퓨터 브라우저에게 보내는 소량의 정보이며 이용자들의 PC 컴퓨터내의 하드디스크에 저장되기도 합니다.</li>
                      </ol>
                      
                      <div className="mt-4 space-y-4">
                        <div className="p-4 bg-cyan-50 rounded-lg">
                          <h4 className="font-semibold mb-2">쿠키의 사용목적</h4>
                          <ul className="space-y-1 text-sm">
                            <li>• 이용자가 방문한 각 서비스와 웹 사이트들에 대한 방문 및 이용형태, 인기 검색어, 보안접속 여부, 등을 파악하여 이용자에게 최적화된 정보 제공을 위해 사용됩니다.</li>
                          </ul>
                        </div>
                        
                        <div className="p-4 bg-orange-50 rounded-lg">
                          <h4 className="font-semibold mb-2">쿠키의 설치·운영 및 거부</h4>
                          <ul className="space-y-1 text-sm">
                            <li>• 웹브라우저 상단의 도구 &gt; 인터넷 옵션 &gt; 개인정보 메뉴의 옵션 설정을 통해 쿠키 저장을 거부할 수 있습니다.</li>
                            <li>• 쿠키 저장을 거부할 경우 맞춤형 서비스 이용에 어려움이 발생할 수 있습니다.</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card className="mt-8">
                <CardHeader>
                  <CardTitle>문의사항</CardTitle>
                  <p className="text-gray-600">
                    개인정보처리방침에 대한 문의사항이 있으시면 아래로 연락해 주세요
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p><strong>회사명:</strong> JHLAB</p>
                    <p><strong>대표이사:</strong> 김재형</p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <strong>이메일:</strong> 
                      <a href="mailto:kjhk3082@hallym.ac.kr" className="text-blue-600 hover:underline">kjhk3082@hallym.ac.kr</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-500" />
                      <strong>전화:</strong> 010-5379-0476
                    </p>
                    <p className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-purple-500" />
                      <strong>주소:</strong> 강원특별자치도 춘천시 한림대학길 1 공학관 1130
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Important Notice */}
      <section className="bg-yellow-50 border-t border-yellow-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">중요 안내</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• 본 약관 및 개인정보처리방침은 {lastUpdated}부터 적용됩니다.</li>
                  <li>• 법령이나 서비스의 변경사항을 반영하기 위한 목적 등으로 개정될 수 있습니다.</li>
                  <li>• 개정 시에는 웹사이트 공지사항을 통해 사전 공지하겠습니다.</li>
                  <li>• 개인정보 처리에 관한 문의는 개인정보보호책임자에게 연락해 주세요.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}